import { deriveCentralPanelRequirement, deriveEdgeFeaturePolicy, deriveLoadRelayModulePolicy, deriveMeteringCascadePolicy, deriveNetmasterRequirement, derivePanelTierPolicy, deriveRemoteIoPanelPolicy, deriveSingleLoadRelayPolicy, deriveThirdPartyAvIntegrationPolicy, makePanelInfrastructureItem } from "@automatry/rules";
import { findProductByPartNo, isHardIoCapabilityEligible } from "@automatry/catalog";
import { getCatalogProducts } from "./controllerAllocation.js";
import { ENGINE_TIMESTAMP, NO_RISK, uniqueSourceRefs } from "./strategyContext.js";
export function deriveInfrastructure(input) {
    const catalogProducts = getCatalogProducts(input.catalog);
    const netmaster = deriveNetmasterRequirement({
        remoteAppRequired: input.triggers.remoteAppRequired,
        meteringRequired: input.triggers.meteringRequired,
        bmsRequired: input.triggers.bmsRequired || input.triggers.thirdPartyAvIntegrationRequired,
        centralMappingRequired: input.triggers.centralMappingRequired || input.triggers.centralScreenRequired,
        alarmVisibilityRequired: input.triggers.alarmVisibilityRequired,
        schedulingRequired: input.triggers.schedulingRequired
    });
    const meteringCascade = deriveMeteringCascadePolicy({
        meteringRequired: input.triggers.meteringRequired,
        meteringHardwareInScope: input.triggers.meteringHardwareInScope,
        protocols: input.triggers.meteringProtocols,
        topology: input.triggers.meteringTopology,
        bmsExposureRequired: input.triggers.meteringBmsExposureRequired,
        topologyNeedsUserDecision: input.triggers.meteringTopologyNeedsUserDecision,
        meterEndpointCount: input.triggers.meterEndpointCount
    });
    const edgePolicy = deriveEdgeFeaturePolicy({
        features: input.triggers.edgeFeatures
    });
    const thirdPartyAvIntegration = deriveThirdPartyAvIntegrationPolicy({
        required: input.triggers.thirdPartyAvIntegrationRequired,
        ...(input.triggers.thirdPartyAvIntegrationProtocol
            ? { protocol: input.triggers.thirdPartyAvIntegrationProtocol }
            : {}),
        sourceRequirementIds: input.triggers.thirdPartyAvIntegrationRequirementIds
    });
    const totalLoadRelayOutputCount = input.loadRelayOutputCounts.individualHighLoadOutputCount +
        input.loadRelayOutputCounts.groupedWufhOutputCount;
    const centralRelayHardwareRequired = totalLoadRelayOutputCount > 0 &&
        (netmaster.quantity > 0 ||
            input.mzControllerCount > 0 ||
            input.triggers.centralMappingRequired ||
            input.triggers.centralScreenRequired ||
            input.triggers.meteringRequired ||
            input.triggers.bmsRequired ||
            input.triggers.alarmVisibilityRequired ||
            input.triggers.schedulingRequired);
    const singleLoadRelays = deriveSingleLoadRelayPolicy(input.loadRelayOutputCounts.individualHighLoadOutputCount);
    const loadRelayModules = deriveLoadRelayModulePolicy(centralRelayHardwareRequired
        ? input.loadRelayOutputCounts.groupedWufhOutputCount
        : 0);
    const remoteIoPanel = deriveRemoteIoPanelPolicy({
        loadRelayOutputCount: totalLoadRelayOutputCount,
        localityCue: input.triggers.remoteIoPanelSuggested
    });
    const centralPanel = deriveCentralPanelRequirement({
        netmasterQuantity: netmaster.quantity,
        ioModuleCount: countSelectors(loadRelayModules.selectors, "load_relay_module"),
        loadRelayOutputCount: centralRelayHardwareRequired ? totalLoadRelayOutputCount : 0,
        mzControllerCount: input.mzControllerCount,
        centralEnableCount: input.triggers.centralMappingRequired ? 1 : 0,
        meteringRequired: input.triggers.meteringRequired,
        bmsRequired: input.triggers.bmsRequired,
        alarmInputCount: input.triggers.alarmVisibilityRequired ? 1 : 0
    });
    const noMeteringRelayPanelWithoutCentralScreen = !input.triggers.meteringRequired &&
        !input.triggers.centralScreenRequired &&
        totalLoadRelayOutputCount > 4;
    const panelTier = derivePanelTierPolicy({
        centralPanel,
        contents: {
            netmasterQuantity: netmaster.quantity,
            mzControllerCount: input.mzControllerCount,
            ioModuleCount: countSelectors(loadRelayModules.selectors, "load_relay_module") +
                countSelectors(meteringCascade.selectors, "meter_interface_module"),
            loadRelayOutputCount: centralRelayHardwareRequired
                ? totalLoadRelayOutputCount
                : 0,
            ...(noMeteringRelayPanelWithoutCentralScreen
                ? { centralScreenRequired: false }
                : {}),
            meteringGatewayCount: countSelectors(meteringCascade.selectors, "metering_gateway"),
            energyMeteringUnitCount: countSelectors(meteringCascade.selectors, "energy_metering_unit"),
            meterInterfaceModuleCount: countSelectors(meteringCascade.selectors, "meter_interface_module")
        }
    });
    const infrastructure = [];
    const risks = [];
    const ruleTraceRefs = [
        ...netmaster.traces.map((trace) => trace.id),
        ...meteringCascade.traces.map((trace) => trace.id),
        ...edgePolicy.traces.map((trace) => trace.id),
        ...thirdPartyAvIntegration.traces.map((trace) => trace.id),
        ...singleLoadRelays.traces.map((trace) => trace.id),
        ...loadRelayModules.traces.map((trace) => trace.id),
        ...remoteIoPanel.traces.map((trace) => trace.id),
        ...centralPanel.traces.map((trace) => trace.id),
        ...panelTier.traces.map((trace) => trace.id)
    ];
    const edgeFeatures = edgePolicy.required
        ? [
            {
                id: `edge-feature-${input.apartmentTypeId}-${edgePolicy.featureKind}`,
                featureKind: edgePolicy.featureKind ?? "leak_detection",
                provisionUnit: edgePolicy.provisionUnit ?? "per_apartment",
                inputPolicy: edgePolicy.inputPolicy ?? "reuse_existing_titan_inputs",
                sourceRequirementIds: edgePolicy.sourceRequirementIds,
                connectivity: "Self-contained Edge gateway PC bridges the existing Titan BACnet network to dashboard and alert workflows.",
                installNotes: edgePolicy.installNotes,
                needsUserDecision: edgePolicy.needsDedicatedSensorDecision,
                why: "Approved Edge feature requirement is represented separately from base Titan controller selection.",
                how: "Derived by @automatry/rules deriveEdgeFeaturePolicy from approved Edge/leak-detection requirements.",
                sourceRefs: input.sourceRefs,
                ruleTraceRefs: edgePolicy.traces.map((trace) => trace.id),
                risk: edgePolicy.needsDedicatedSensorDecision
                    ? {
                        level: "warning",
                        riskRefs: [`risk-${input.apartmentTypeId}-edge-dedicated-leak-sensors`],
                        notes: "Default is to reuse existing Titan/BACnet inputs; confirm before adding dedicated leak sensor hardware."
                    }
                    : NO_RISK
            }
        ]
        : [];
    if (input.triggers.meteringRequired && !input.triggers.meteringHardwareInScope) {
        infrastructure.push(makeMeteringScopeExclusionItem({
            apartmentTypeId: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: meteringCascade.traces.map((trace) => trace.id),
            installNotes: meteringCascade.installNotes
        }));
    }
    if (input.triggers.ccsEnergyDisplayRequired) {
        edgeFeatures.push({
            id: `edge-feature-${input.apartmentTypeId}-ccs-energy-display`,
            featureKind: "metering_dashboard",
            provisionUnit: "per_apartment",
            inputPolicy: "reuse_existing_titan_inputs",
            sourceRequirementIds: input.triggers.meteringRequirementIds,
            connectivity: input.triggers.meteringTopology === "netmaster_direct"
                ? "CCS displays tariff, cost, kWh, and kW values from the TP-NM-R-EM-D NetMaster energy-monitoring application over the apartment controls network."
                : "CCS displays energy values supplied by the central metering cascade over the apartment controls network.",
            installNotes: [
                "Resident energy display is a CCS/NetMaster feature; billing meter provision remains by others unless explicitly scoped."
            ],
            needsUserDecision: false,
            why: "The approved metering requirement asks for resident energy information to be shown on the CCS.",
            how: "Derived from metering requirement text that mentions CCS resident energy/tariff/cost/kWh/kW display.",
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: ["trace-metering-ccs-energy-display"],
            risk: NO_RISK
        });
    }
    if (meteringCascade.needsUserDecision) {
        risks.push(makeRisk({
            id: `risk-${input.apartmentTypeId}-metering-topology-default`,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "Metering Modbus assumption needs confirmation",
            plainEnglish: "Metering is required but the approved inputs do not specify the protocol/topology. The engine assumes Modbus-on-NetMaster, so the NetMaster reads the meters directly and no MB-10 gateway, TP-EM-16 EMS, IO-COM-EM8 meter-interface module, or metering enclosure is counted. This is a stated assumption, not a blocker.",
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: ["trace-metering-topology-default-modbus-netmaster"],
            action: "Confirm the meters include Modbus cards for NetMaster direct reading, or raise an RFI for the metering protocol/topology."
        }));
    }
    if (meteringCascade.topology === "netmaster_direct" &&
        input.triggers.meteringMeterProducts.length === 0) {
        risks.push(makeRisk({
            id: `risk-${input.apartmentTypeId}-metering-meter-products`,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "By-others meter product identities need confirmation",
            plainEnglish: "The requirement selects NetMaster-direct Modbus metering, but the meter products on the bus were not named. The engine did not invent a meter SKU.",
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: ["trace-metering-netmaster-direct-by-others-meters"],
            action: "Confirm the by-others Modbus meter product(s), such as electricity and heat/cool meter models."
        }));
    }
    if (netmaster.quantity > 0 && netmaster.productPartNo) {
        const netmasterProduct = findProductByPartNo(catalogProducts, netmaster.productPartNo);
        const netmasterDirectMetering = meteringCascade.topology === "netmaster_direct" &&
            netmaster.productPartNo === "TP-NM-R-EM-D";
        infrastructure.push({
            id: `infrastructure-${input.apartmentTypeId}-netmaster`,
            itemKind: "netmaster",
            supplySource: "titan_supplied",
            installIncluded: false,
            quantity: 1,
            productPartNo: netmaster.productPartNo,
            ...(netmasterProduct
                ? {
                    protocols: [...netmasterProduct.capabilities.protocols],
                    sourceStatus: netmasterProduct.sourceStatus
                }
                : {}),
            ...(netmasterDirectMetering
                ? {
                    connectivity: "TP-NM-R-EM-D is the apartment Modbus RTU master for by-others meters, publishes metering data over BACnet/IP where required, and feeds CCS resident energy display where required.",
                    installNotes: [
                        ...meteringCascade.installNotes,
                        ...(input.triggers.meteringBmsExposureRequired
                            ? [
                                "Expose NetMaster metering objects to the landlord BMS over BACnet/IP."
                            ]
                            : []),
                        ...(input.triggers.ccsEnergyDisplayRequired
                            ? [
                                "Enable CCS resident energy/tariff/cost/kWh/kW display from the NetMaster metering application."
                            ]
                            : [])
                    ],
                    capabilityRole: "modbus_metering_master"
                }
                : {}),
            why: "An approved requirement triggers supervisory mapping/integration.",
            how: netmasterDirectMetering
                ? "Derived through @automatry/rules deriveNetmasterRequirement and deriveMeteringCascadePolicy; NetMaster contributes zero hard IO and covers the Modbus RTU metering-master role without a standalone gateway/EMS/interface module."
                : "Derived through @automatry/rules deriveNetmasterRequirement; Netmaster contributes zero hard IO and its catalog capabilities remain available for protocol roles.",
            sourceRefs: netmasterProduct
                ? uniqueSourceRefs([...input.sourceRefs, ...netmasterProduct.sourceRefs])
                : input.sourceRefs,
            ruleTraceRefs: [
                ...netmaster.traces.map((trace) => trace.id),
                ...(netmasterDirectMetering ? ["trace-metering-netmaster-direct-topology"] : [])
            ],
            risk: NO_RISK
        });
    }
    else if (netmaster.requiresUserDecisionWhenNoTrigger) {
        risks.push(makeRisk({
            id: `risk-${input.apartmentTypeId}-netmaster-default`,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "Netmaster default is unresolved",
            plainEnglish: "No approved requirement triggers a Netmaster, so the engine omits it and records the open default decision.",
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: netmaster.traces.map((trace) => trace.id),
            action: "Confirm whether Netmaster should be a hard default for this apartment type."
        }));
    }
    for (const selector of edgePolicy.selectors) {
        const resolved = makeSelectorInfrastructureItem({
            apartmentTypeId: input.apartmentTypeId,
            projectId: input.projectId,
            selector,
            catalogProducts,
            existingInfrastructure: infrastructure,
            sourceRefs: input.sourceRefs
        });
        infrastructure.push(resolved.item);
        if (resolved.risk) {
            risks.push(resolved.risk);
        }
    }
    if (edgePolicy.needsDedicatedSensorDecision) {
        risks.push(makeRisk({
            id: `risk-${input.apartmentTypeId}-edge-dedicated-leak-sensors`,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "Dedicated leak sensor scope is unresolved",
            plainEnglish: "Edge leak detection is modelled as reusing existing Titan/BACnet inputs by default. Confirm whether this project also needs dedicated leak sensors or leak rope before adding sensor hardware.",
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: ["trace-edge-leak-detection-input-policy"],
            action: "Confirm whether leak detection reuses existing Titan inputs or requires dedicated leak sensor hardware."
        }));
    }
    for (const selector of thirdPartyAvIntegration.selectors) {
        const resolved = makeSelectorInfrastructureItem({
            apartmentTypeId: input.apartmentTypeId,
            projectId: input.projectId,
            selector,
            catalogProducts,
            existingInfrastructure: infrastructure,
            sourceRefs: input.sourceRefs
        });
        infrastructure.push(resolved.item);
        if (resolved.risk) {
            risks.push(resolved.risk);
        }
    }
    for (const selector of meteringCascade.selectors) {
        const resolved = makeSelectorInfrastructureItem({
            apartmentTypeId: input.apartmentTypeId,
            projectId: input.projectId,
            selector,
            catalogProducts,
            existingInfrastructure: infrastructure,
            sourceRefs: input.sourceRefs
        });
        infrastructure.push(resolved.item);
        if (resolved.risk) {
            risks.push(resolved.risk);
        }
    }
    for (const selector of singleLoadRelays.selectors) {
        const resolved = makeSelectorInfrastructureItem({
            apartmentTypeId: input.apartmentTypeId,
            projectId: input.projectId,
            selector,
            catalogProducts,
            existingInfrastructure: infrastructure,
            sourceRefs: input.sourceRefs
        });
        infrastructure.push(resolved.item);
        if (resolved.risk) {
            risks.push(resolved.risk);
        }
    }
    for (const selector of loadRelayModules.selectors) {
        const resolved = makeSelectorInfrastructureItem({
            apartmentTypeId: input.apartmentTypeId,
            projectId: input.projectId,
            selector,
            catalogProducts,
            existingInfrastructure: infrastructure,
            sourceRefs: input.sourceRefs
        });
        infrastructure.push(resolved.item);
        if (resolved.risk) {
            risks.push(resolved.risk);
        }
    }
    if (remoteIoPanel.suggested) {
        const riskId = `risk-${input.apartmentTypeId}-remote-io-panel-decision`;
        risks.push(makeRisk({
            id: riskId,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "Remote relay enclosure placement needs confirmation",
            plainEnglish: "Load relay outputs have a bathroom/remote locality cue. The relay products remain exact SRM/SP or IO/COM rows, while any local enclosure count/location stays a decision until current evidence names a catalogued panel or enclosure.",
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: remoteIoPanel.traces.map((trace) => trace.id),
            action: "Confirm whether these load relays should sit in a remote enclosure and, if so, identify the exact enclosure product/count."
        }));
    }
    const panelItem = makePanelInfrastructureItem(centralPanel);
    if (panelItem && panelTier.selector) {
        const resolved = makeSelectorInfrastructureItem({
            apartmentTypeId: input.apartmentTypeId,
            projectId: input.projectId,
            selector: panelTier.selector,
            catalogProducts,
            existingInfrastructure: infrastructure,
            sourceRefs: input.sourceRefs,
            idSuffix: "central-panel",
            whyOverride: `Central panel tier ${panelTier.tierId} is required by central contents.`,
            howOverride: `${panelItem.how} Tier policy rank ${panelTier.tierRank}; contents: ${JSON.stringify(panelTier.contents)}.`
        });
        const suppressUnscopedMeteringEnclosure = input.triggers.meteringRequired &&
            !input.triggers.meteringHardwareInScope &&
            resolved.item.productPartNo === "TP-NM-R-D-EM-ENC";
        const suppressUnverifiedDefaultMeteringEnclosure = input.triggers.meteringTopologyNeedsUserDecision &&
            input.triggers.meteringTopology === "netmaster_direct" &&
            resolved.item.productPartNo === "TP-NM-R-D-EM-ENC";
        const suppressMeteringEnclosure = suppressUnscopedMeteringEnclosure || suppressUnverifiedDefaultMeteringEnclosure;
        const unresolvedNoCentralScreenPanel = panelTier.tierId === "base_netmaster_panel" &&
            panelTier.contents.centralScreenRequired === false &&
            resolved.item.selectionStatus === "placeholder";
        if (!unresolvedNoCentralScreenPanel) {
            infrastructure.push({
                ...panelItem,
                id: `infrastructure-${input.apartmentTypeId}-central-panel`,
                itemKind: resolved.item.itemKind,
                supplySource: resolved.item.supplySource,
                ...(suppressMeteringEnclosure
                    ? {}
                    : { productPartNo: resolved.item.productPartNo }),
                protocols: resolved.item.protocols,
                connectivity: resolved.item.connectivity,
                selectionStatus: suppressMeteringEnclosure
                    ? "covered_by_existing"
                    : resolved.item.selectionStatus,
                capabilityRole: resolved.item.capabilityRole,
                coveredByInfrastructureItemId: resolved.item.coveredByInfrastructureItemId,
                placeholderName: resolved.item.placeholderName,
                placeholderDescription: resolved.item.placeholderDescription,
                sourceStatus: resolved.item.sourceStatus,
                installNotes: suppressMeteringEnclosure
                    ? suppressUnverifiedDefaultMeteringEnclosure
                        ? [
                            "Expanded controls panel need is retained as scope context, but TP-NM-R-D-EM-ENC is a metering enclosure SKU and is not counted while metering protocol/topology is only the Modbus-on-NetMaster assumption."
                        ]
                        : [
                            "Expanded controls panel need is retained as scope context, but TP-NM-R-D-EM-ENC is a metering enclosure SKU and is not counted without explicit Titan metering hardware scope."
                        ]
                    : resolved.item.installNotes,
                why: suppressMeteringEnclosure
                    ? "Central controls still need panel coordination, but the only reviewed expanded SKU is a metering enclosure that is not proven in scope."
                    : resolved.item.why,
                how: suppressMeteringEnclosure
                    ? `${resolved.item.how} Product selection suppressed by ${suppressUnverifiedDefaultMeteringEnclosure
                        ? "trace-metering-topology-default-modbus-netmaster"
                        : "trace-metering-hardware-out-of-scope"}.`
                    : resolved.item.how,
                sourceRefs: resolved.item.sourceRefs,
                ruleTraceRefs: [
                    ...new Set([
                        ...centralPanel.traces.map((trace) => trace.id),
                        ...resolved.item.ruleTraceRefs,
                        ...(suppressUnscopedMeteringEnclosure
                            ? ["trace-metering-hardware-out-of-scope"]
                            : []),
                        ...(suppressUnverifiedDefaultMeteringEnclosure
                            ? ["trace-metering-topology-default-modbus-netmaster"]
                            : [])
                    ])
                ],
                risk: resolved.item.risk
            });
        }
        if (resolved.risk) {
            risks.push(resolved.risk);
        }
    }
    else if (panelItem) {
        const riskId = `risk-${input.apartmentTypeId}-panel-tier-product`;
        infrastructure.push({
            ...panelItem,
            id: `infrastructure-${input.apartmentTypeId}-central-panel`,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: panelTier.traces.map((trace) => trace.id),
            risk: {
                level: "warning",
                riskRefs: [riskId],
                notes: "Central panel is required, but the rule tier has no reviewed catalog SKU selector."
            }
        });
        risks.push(makeRisk({
            id: riskId,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "Panel tier SKU is unresolved",
            plainEnglish: "Central kit is present, but the selected panel tier has no reviewed catalog SKU.",
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: panelTier.traces.map((trace) => trace.id),
            action: "Confirm the catalog SKU for this central panel tier."
        }));
    }
    return {
        edgeFeatures,
        infrastructure,
        risks,
        ruleTraceRefs: [...new Set(ruleTraceRefs)]
    };
}
function makeMeteringScopeExclusionItem(input) {
    return {
        id: `infrastructure-${input.apartmentTypeId}-metering-by-others-note`,
        itemKind: "cabling_note",
        supplySource: "third_party",
        installIncluded: false,
        quantity: 1,
        commercialModel: "install_note",
        selectionStatus: "covered_by_existing",
        installNotes: input.installNotes,
        why: "The approved metering evidence does not put Titan metering gateway, EMS, meter-interface, or metering enclosure hardware in scope.",
        how: "Derived from pulse-only and/or by-others metering evidence; kept as a scope exclusion note rather than a counted hardware line.",
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        risk: NO_RISK
    };
}
function makeRisk(input) {
    return {
        id: input.id,
        projectId: input.projectId,
        type: input.type,
        severity: input.severity,
        scope: {
            level: "apartment_type",
            refId: input.scopeRef
        },
        title: input.title,
        plainEnglish: input.plainEnglish,
        technicalTrace: input.sourceRefs,
        recommendedActions: [
            {
                action: input.action,
                owner: "estimator",
                status: "open"
            }
        ],
        status: "open",
        blocksExport: input.severity === "blocker",
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        createdAt: ENGINE_TIMESTAMP
    };
}
function countSelectors(selectors, role) {
    return selectors
        .filter((selector) => selector.role === role)
        .reduce((total, selector) => total + selector.quantity, 0);
}
function makeSelectorInfrastructureItem(input) {
    const existingCapability = input.selector.role === "load_relay_module" ||
        input.selector.role === "single_load_relay"
        ? undefined
        : resolveExistingCapability(input.selector, input.catalogProducts, input.existingInfrastructure);
    const product = resolveReviewedProduct(input.selector, input.catalogProducts);
    const riskId = `risk-${input.apartmentTypeId}-${input.selector.id}-product`;
    const selectedProduct = existingCapability?.product ?? product;
    const sourceRefs = selectedProduct
        ? uniqueSourceRefs([...input.sourceRefs, ...selectedProduct.sourceRefs])
        : input.sourceRefs;
    const itemId = `infrastructure-${input.apartmentTypeId}-${input.idSuffix ?? input.selector.id}`;
    const primaryCapabilityRole = input.selector.capabilityRoles[0];
    if (existingCapability) {
        return {
            item: {
                id: itemId,
                itemKind: input.selector.itemKind,
                supplySource: existingCapability.product.supplySource,
                installIncluded: input.selector.installIncluded,
                quantity: input.selector.quantity,
                productPartNo: existingCapability.product.partNo,
                protocols: [...input.selector.protocols],
                connectivity: input.selector.connectivity,
                ...(input.selector.provisionScope
                    ? { provisionScope: input.selector.provisionScope }
                    : {}),
                ...(input.selector.commercialModel
                    ? { commercialModel: input.selector.commercialModel }
                    : {}),
                ...(input.selector.billingPeriod
                    ? { billingPeriod: input.selector.billingPeriod }
                    : {}),
                ...(input.selector.installNotes?.length
                    ? { installNotes: [...input.selector.installNotes] }
                    : {}),
                ...(input.selector.alternativePartNos?.length
                    ? { alternativeProductPartNos: [...input.selector.alternativePartNos] }
                    : {}),
                selectionStatus: "covered_by_existing",
                ...(primaryCapabilityRole ? { capabilityRole: primaryCapabilityRole } : {}),
                coveredByInfrastructureItemId: existingCapability.item.id,
                sourceStatus: existingCapability.product.sourceStatus,
                why: input.whyOverride ?? input.selector.why,
                how: input.howOverride ??
                    `${input.selector.how} Covered by existing ${existingCapability.product.partNo} (${existingCapability.item.id}) using catalog roles ${existingCapability.product.capabilities.roles.join(", ")}; no additional product is selected.`,
                sourceRefs,
                ruleTraceRefs: [input.selector.traceId],
                risk: NO_RISK
            }
        };
    }
    const item = {
        id: itemId,
        itemKind: input.selector.itemKind,
        supplySource: product?.supplySource ?? input.selector.supplySource,
        installIncluded: input.selector.installIncluded,
        quantity: input.selector.quantity,
        ...(product ? { productPartNo: product.partNo } : {}),
        protocols: [...input.selector.protocols],
        connectivity: input.selector.connectivity,
        ...(input.selector.provisionScope
            ? { provisionScope: input.selector.provisionScope }
            : {}),
        ...(input.selector.commercialModel
            ? { commercialModel: input.selector.commercialModel }
            : {}),
        ...(input.selector.billingPeriod
            ? { billingPeriod: input.selector.billingPeriod }
            : {}),
        ...(input.selector.installNotes?.length
            ? { installNotes: [...input.selector.installNotes] }
            : {}),
        ...(input.selector.alternativePartNos?.length
            ? { alternativeProductPartNos: [...input.selector.alternativePartNos] }
            : {}),
        selectionStatus: product ? "selected" : "placeholder",
        ...(primaryCapabilityRole ? { capabilityRole: primaryCapabilityRole } : {}),
        ...(product
            ? { sourceStatus: product.sourceStatus }
            : {
                placeholderName: input.selector.placeholderName,
                placeholderDescription: input.selector.placeholderDescription,
                sourceStatus: "research-needed"
            }),
        why: input.whyOverride ?? input.selector.why,
        how: input.howOverride ??
            (product
                ? input.selector.how
                : `${input.selector.how} No existing capability or known catalog product matched, so this is a named placeholder and not a selected SKU.`),
        sourceRefs,
        ruleTraceRefs: [input.selector.traceId],
        risk: product
            ? NO_RISK
            : {
                level: "warning",
                riskRefs: [riskId, input.selector.missingProductRiskRef],
                notes: `No reviewed catalog product matched selector ${input.selector.id}; product role is emitted as a named placeholder rather than guessed.`
            }
    };
    if (product) {
        return { item };
    }
    return {
        item,
        risk: makeRisk({
            id: riskId,
            projectId: input.projectId,
            type: "needsUserDecision",
            severity: "warning",
            title: "Infrastructure placeholder needs a user decision",
            plainEnglish: `${input.selector.placeholderName} is required, but no existing capability and no known catalog or third-party product matched. The engine emitted a placeholder only and did not invent a SKU.`,
            scopeRef: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs: [input.selector.traceId],
            action: `Confirm the product mapping for ${input.selector.role}.`
        })
    };
}
function resolveReviewedProduct(selector, products) {
    const preferred = new Set(selector.preferredPartNos.map((partNo) => partNo.toUpperCase()));
    for (const partNo of selector.preferredPartNos) {
        const product = findProductByPartNo(products, partNo);
        if (product &&
            isHardIoCapabilityEligible(product) &&
            productMatchesSelector(product, selector)) {
            return product;
        }
    }
    return [...products]
        .filter((product) => !preferred.has(product.partNo.toUpperCase()) &&
        isHardIoCapabilityEligible(product) &&
        productMatchesSelector(product, selector))
        .sort((left, right) => left.partNo.localeCompare(right.partNo))[0];
}
function productMatchesSelector(product, selector) {
    if (!productCanFulfilSelectorCapability(product, selector)) {
        return false;
    }
    const searchableText = [
        product.partNo,
        ...product.aliases,
        product.family,
        product.description,
        product.capabilities.primaryApplication ?? "",
        ...product.capabilities.protocols,
        ...product.capabilities.roles,
        ...product.capabilities.notes
    ]
        .join(" ")
        .toLowerCase();
    return selector.requiredTextMatches.every((needle) => searchableText.includes(needle.toLowerCase()));
}
function resolveExistingCapability(selector, products, infrastructure) {
    for (const item of infrastructure) {
        if (!item.productPartNo || item.selectionStatus === "placeholder") {
            continue;
        }
        const product = findProductByPartNo(products, item.productPartNo);
        if (product &&
            isHardIoCapabilityEligible(product) &&
            productCanFulfilSelectorCapability(product, selector)) {
            return { item, product };
        }
    }
    return undefined;
}
function productCanFulfilSelectorCapability(product, selector) {
    const productRoles = product.capabilities.roles.map(normalizeCapabilityText);
    const selectorRoles = selector.capabilityRoles.map(normalizeCapabilityText);
    const roleMatch = selectorRoles.length === 0 ||
        selectorRoles.some((role) => productRoles.includes(role));
    return roleMatch && protocolsCoverSelector(product, selector);
}
function protocolsCoverSelector(product, selector) {
    const productProtocols = product.capabilities.protocols.map(normalizeCapabilityText);
    return selector.protocols.every((requiredProtocol) => {
        const required = normalizeCapabilityText(requiredProtocol);
        return productProtocols.some((protocol) => protocol.includes(required) || required.includes(protocol));
    });
}
function normalizeCapabilityText(value) {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}
//# sourceMappingURL=infrastructure.js.map